import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAdminApp } from "@/hooks/useAdminApp";
import { ROUTES } from "@/routes";
import { StatusBadge } from "./components/StatusBadge";
import { StepTimeline } from "./components/StepTimeline";
import { ErrorBanner } from "./components/ErrorBanner";
import { Button } from "@/components/ui/base/button";
import { Loader2, ArrowLeft } from "lucide-react";
import type {
  ProvisioningRequest,
  ProvisionInput,
  AddressData,
} from "@/application/provisioning/types";
import toast from "react-hot-toast";
import { connectedAccountUrl } from "@/lib/stripe-links";
import { TransactionsList } from "@/presentation/money/TransactionsList";
import { FeesTab } from "@/presentation/fees/FeesTab";
import { TerminalSettingsTab } from "@/presentation/settings/TerminalSettingsTab";

type EditableFields = Omit<
  ProvisionInput,
  "businessAddress" | "individualAddress"
> & {
  businessAddress: Partial<AddressData>;
  individualAddress: Partial<AddressData>;
};

type DetailTab = "overview" | "transactions" | "fees" | "settings";

function isValidTab(v: string | null): v is DetailTab {
  return (
    v === "overview" || v === "transactions" || v === "fees" || v === "settings"
  );
}

const TABS: { id: DetailTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "transactions", label: "Transactions" },
  { id: "fees", label: "Fees" },
  { id: "settings", label: "Settings" },
];

export function AccountDetailView() {
  const { id } = useParams<{ id: string }>();
  const { provisioningRepo } = useAdminApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab");
  const activeTab: DetailTab = isValidTab(rawTab) ? rawTab : "overview";

  function handleTabChange(tab: DetailTab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true },
    );
  }
  const [record, setRecord] = useState<ProvisioningRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState<EditableFields | null>(null);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resending, setResending] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await provisioningRepo.get(id);
      setRecord(data);
    } catch {
      toast.error("Failed to load account");
    } finally {
      setLoading(false);
    }
  }, [id, provisioningRepo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async () => {
    if (!id) return;
    setRetrying(true);
    try {
      const result = await provisioningRepo.retry(id);
      if (result.errors?.length) {
        toast.error(result.errors[0]);
      } else if (result.status === "completed") {
        toast.success("Account provisioned successfully!");
      } else {
        toast.error(`Failed at: ${result.failedStep}`);
      }
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const handleEditStart = () => {
    if (!record) return;
    setEditFields({
      email: record.email,
      phone: record.phone,
      firstName: record.firstName,
      lastName: record.lastName,
      birthDate: record.birthDate,
      businessName: record.businessName,
      businessCountry: record.businessCountry,
      businessType: record.businessType,
      businessCategoryCode: record.businessCategoryCode,
      businessAddress: { ...record.businessAddress },
      individualAddress: { ...record.individualAddress },
      ssnLast4: record.ssnLast4,
      taxId: record.taxId,
      businessUrl: record.businessUrl,
      supportPhone: record.supportPhone,
    });
    setEditing(true);
  };

  const handleEditSave = async () => {
    if (!id || !editFields) return;
    setSaving(true);
    try {
      const result = await provisioningRepo.update(
        id,
        editFields as ProvisionInput,
      );
      if (result.errors?.length) {
        toast.error(result.errors[0] ?? "Update failed");
      } else {
        toast.success("Details updated. Affected steps will re-run on retry.");
        setEditing(false);
        await load();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    if (
      !window.confirm(
        "Cancel this provisioning request? This cannot be undone.",
      )
    )
      return;
    setCancelling(true);
    try {
      await provisioningRepo.cancel(id);
      toast.success("Provisioning request cancelled.");
      navigate(ROUTES.TERMINAL_ACCOUNTS);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const handleResendWelcomeEmail = async () => {
    if (!id || !record) return;
    setResending(true);
    try {
      const result = await provisioningRepo.resendWelcomeEmail(id);
      const errMsg = result.errors?.find(
        (e): e is string => typeof e === "string" && e.length > 0,
      );
      if (errMsg) {
        toast.error(errMsg);
      } else if (result.success) {
        toast.success(`Welcome email sent to ${record.email}.`);
      } else {
        toast.error("Failed to send welcome email");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Resend failed");
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="text-muted-foreground text-center">
        Account not found.{" "}
        <button
          className="text-blue-600"
          onClick={() => navigate(ROUTES.TERMINAL_ACCOUNTS)}
        >
          Back to list
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm"
        onClick={() => navigate(ROUTES.TERMINAL_ACCOUNTS)}
      >
        <ArrowLeft className="h-4 w-4" /> Back to accounts
      </button>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-foreground text-2xl font-bold">
            {record.businessName}
          </h1>
          <p className="text-muted-foreground text-sm">
            {record.email} · {record.phone}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={record.status} />
          {record.status === "failed" && (
            <>
              <Button onClick={handleRetry} disabled={retrying}>
                {retrying ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Retry
              </Button>
              <Button variant="outline" onClick={handleEditStart}>
                Edit Details
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Cancel
              </Button>
            </>
          )}
          {record.status === "completed" && (
            <Button
              variant="outline"
              onClick={handleResendWelcomeEmail}
              disabled={resending}
            >
              {resending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Resend Welcome Email
            </Button>
          )}
        </div>
      </div>

      {/* Tab strip */}
      <div className="mb-6 flex gap-1 border-b">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab.id)}
            className={[
              "px-4 py-2 text-sm font-medium transition-colors",
              activeTab === tab.id
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-muted-foreground hover:text-foreground",
            ].join(" ")}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {activeTab === "overview" && (
        <div>
          {record.status === "failed" &&
            record.failedStep &&
            record.failedError && (
              <div className="mb-6">
                <ErrorBanner
                  failedStep={record.failedStep}
                  failedError={record.failedError}
                  retryCount={record.retryCount}
                  updatedAt={record.updatedAt}
                />
              </div>
            )}

          {editing && editFields && (
            <div className="bg-card mb-6 rounded-lg border p-4">
              <h2 className="text-foreground mb-4 text-sm font-semibold">
                Edit Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <EditField
                  label="First Name"
                  value={editFields.firstName}
                  onChange={(v) =>
                    setEditFields({ ...editFields, firstName: v })
                  }
                />
                <EditField
                  label="Last Name"
                  value={editFields.lastName}
                  onChange={(v) =>
                    setEditFields({ ...editFields, lastName: v })
                  }
                />
                <EditField
                  label="Email"
                  value={editFields.email}
                  onChange={(v) => setEditFields({ ...editFields, email: v })}
                />
                <EditField
                  label="Phone"
                  value={editFields.phone}
                  onChange={(v) => setEditFields({ ...editFields, phone: v })}
                />
                <EditField
                  label="Business Name"
                  value={editFields.businessName}
                  onChange={(v) =>
                    setEditFields({ ...editFields, businessName: v })
                  }
                />
                <EditField
                  label="Date of Birth"
                  value={editFields.birthDate}
                  onChange={(v) =>
                    setEditFields({ ...editFields, birthDate: v })
                  }
                />
                <EditField
                  label="Category Code"
                  value={editFields.businessCategoryCode}
                  onChange={(v) =>
                    setEditFields({ ...editFields, businessCategoryCode: v })
                  }
                />
                <EditField
                  label="Business URL"
                  value={editFields.businessUrl ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      businessUrl: v || undefined,
                    })
                  }
                />
                <EditField
                  label="Support Phone"
                  value={editFields.supportPhone ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      supportPhone: v || undefined,
                    })
                  }
                />
                {editFields.businessCountry === "US" && (
                  <EditField
                    label="SSN Last 4"
                    value={editFields.ssnLast4 ?? ""}
                    onChange={(v) =>
                      setEditFields({ ...editFields, ssnLast4: v || undefined })
                    }
                  />
                )}
                {editFields.businessCountry === "CA" && (
                  <EditField
                    label="Tax ID (BN)"
                    value={editFields.taxId ?? ""}
                    onChange={(v) =>
                      setEditFields({ ...editFields, taxId: v || undefined })
                    }
                  />
                )}
              </div>

              <h3 className="text-muted-foreground mt-4 mb-2 text-xs font-semibold">
                Business Address
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <EditField
                  label="Line 1"
                  value={editFields.businessAddress.line1 ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      businessAddress: {
                        ...editFields.businessAddress,
                        line1: v,
                      },
                    })
                  }
                />
                <EditField
                  label="Line 2"
                  value={editFields.businessAddress.line2 ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      businessAddress: {
                        ...editFields.businessAddress,
                        line2: v || undefined,
                      },
                    })
                  }
                />
                <EditField
                  label="City"
                  value={editFields.businessAddress.city ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      businessAddress: {
                        ...editFields.businessAddress,
                        city: v,
                      },
                    })
                  }
                />
                <EditField
                  label="State / Province"
                  value={editFields.businessAddress.state ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      businessAddress: {
                        ...editFields.businessAddress,
                        state: v,
                      },
                    })
                  }
                />
                <EditField
                  label="Postal Code"
                  value={editFields.businessAddress.postalCode ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      businessAddress: {
                        ...editFields.businessAddress,
                        postalCode: v,
                      },
                    })
                  }
                />
              </div>

              <h3 className="text-muted-foreground mt-4 mb-2 text-xs font-semibold">
                Individual Address
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <EditField
                  label="Line 1"
                  value={editFields.individualAddress.line1 ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      individualAddress: {
                        ...editFields.individualAddress,
                        line1: v,
                      },
                    })
                  }
                />
                <EditField
                  label="Line 2"
                  value={editFields.individualAddress.line2 ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      individualAddress: {
                        ...editFields.individualAddress,
                        line2: v || undefined,
                      },
                    })
                  }
                />
                <EditField
                  label="City"
                  value={editFields.individualAddress.city ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      individualAddress: {
                        ...editFields.individualAddress,
                        city: v,
                      },
                    })
                  }
                />
                <EditField
                  label="State / Province"
                  value={editFields.individualAddress.state ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      individualAddress: {
                        ...editFields.individualAddress,
                        state: v,
                      },
                    })
                  }
                />
                <EditField
                  label="Postal Code"
                  value={editFields.individualAddress.postalCode ?? ""}
                  onChange={(v) =>
                    setEditFields({
                      ...editFields,
                      individualAddress: {
                        ...editFields.individualAddress,
                        postalCode: v,
                      },
                    })
                  }
                />
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button onClick={handleEditSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Save
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-card rounded-lg border p-4">
              <h2 className="text-foreground mb-3 text-sm font-semibold">
                Provisioning Steps
              </h2>
              <StepTimeline
                currentStep={record.currentStep}
                failedStep={record.failedStep}
                stepHistory={record.stepHistory}
                status={record.status}
              />
            </div>

            <div className="space-y-4">
              {(record.cognitoUserId ||
                record.businessProfileId ||
                record.stripeConnectedAccountId) && (
                <div className="bg-card rounded-lg border p-4">
                  <h2 className="text-foreground mb-3 text-sm font-semibold">
                    Account References
                  </h2>
                  <div className="space-y-2 text-sm">
                    {record.cognitoUserId && (
                      <RefRow
                        label="Cognito User"
                        value={record.cognitoUserId}
                      />
                    )}
                    {record.businessProfileId && (
                      <RefRow
                        label="Business Profile"
                        value={record.businessProfileId}
                      />
                    )}
                    {record.stripeConnectedAccountId && (
                      <RefRow
                        label="Stripe Account"
                        value={record.stripeConnectedAccountId}
                        href={connectedAccountUrl(
                          record.stripeConnectedAccountId,
                        )}
                      />
                    )}
                    {record.stripeTerminalLocationId && (
                      <RefRow
                        label="Terminal Location"
                        value={record.stripeTerminalLocationId}
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="bg-card rounded-lg border p-4">
                <h2 className="text-foreground mb-3 text-sm font-semibold">
                  Business Details
                </h2>
                <div className="space-y-2 text-sm">
                  <RefRow
                    label="Country"
                    value={
                      record.businessCountry === "CA"
                        ? "\u{1F1E8}\u{1F1E6} Canada"
                        : "\u{1F1FA}\u{1F1F8} United States"
                    }
                  />
                  <RefRow label="Type" value={record.businessType} />
                  <RefRow
                    label="Category"
                    value={record.businessCategoryCode}
                  />
                </div>
              </div>

              <div className="bg-card rounded-lg border p-4">
                <h2 className="text-foreground mb-3 text-sm font-semibold">
                  Audit
                </h2>
                <div className="space-y-2 text-sm">
                  <RefRow label="Created by" value={record.createdByEmail} />
                  <RefRow
                    label="Created"
                    value={new Date(record.createdAt).toLocaleString()}
                  />
                  <RefRow label="Source" value={record.source} />
                  {record.launchpadApplicationId && (
                    <RefRow
                      label="Launchpad Application"
                      value={record.launchpadApplicationId}
                    />
                  )}
                  <RefRow label="Retries" value={String(record.retryCount)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transactions tab */}
      {activeTab === "transactions" && (
        <div>
          {record.businessProfileId ? (
            <TransactionsList
              businessId={record.businessProfileId}
              txDetailRoute={ROUTES.TERMINAL_ACCOUNT_TRANSACTION_DETAIL}
              txDetailParams={{ id: id ?? "" }}
            />
          ) : (
            <div className="bg-card text-muted-foreground flex items-center justify-center rounded-lg border p-12 text-center">
              <p className="text-sm">
                Transactions will appear here once this account is provisioned.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Fees tab */}
      {activeTab === "fees" && (
        <div>
          {record.businessProfileId ? (
            <FeesTab businessId={record.businessProfileId} />
          ) : (
            <div className="bg-card text-muted-foreground flex items-center justify-center rounded-lg border p-12 text-center">
              <p className="text-sm">
                Fees will be configurable once this account is provisioned.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Settings tab */}
      {activeTab === "settings" && (
        <div>
          {record.businessProfileId ? (
            <TerminalSettingsTab businessId={record.businessProfileId} />
          ) : (
            <div className="bg-card text-muted-foreground flex items-center justify-center rounded-lg border p-12 text-center">
              <p className="text-sm">
                Settings will be configurable once this account is provisioned.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="text-muted-foreground mb-1 block text-xs font-medium">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-border text-foreground w-full rounded-md border px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
      />
    </div>
  );
}

function RefRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs text-blue-600 underline"
        >
          {value.length > 16 ? `${value.slice(0, 16)}...` : value}
        </a>
      ) : (
        <span className="text-foreground font-mono text-xs">
          {value.length > 24 ? `${value.slice(0, 24)}...` : value}
        </span>
      )}
    </div>
  );
}
